import React, { useMemo } from "react";
import { Box, Typography, withStyles, useMediaQuery } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";

// Home-page "This week" strip: seven day columns (today first) fed by
// window.week_agenda — lessons carry ISO timestamps (converted to the
// viewer's timezone), class sessions carry wall-clock times on a date
// (rendered as-is, matching My Calendar's behavior).

const StripWrap = withStyles((theme) => ({
  root: {
    border: "1px solid #E0E0E0",
    borderRadius: 16,
    boxShadow: "0 4px 6px rgba(0,0,0,.05)",
    background: "#ffffff",
    padding: "16px 18px 14px",
    marginBottom: 24,
    [theme.breakpoints.down("sm")]: {
      padding: "12px 14px 12px",
      marginBottom: 16,
    },
  },
}))(Box);

const StripHeader = withStyles(() => ({
  root: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },
}))(Box);

const DayRow = withStyles((theme) => ({
  root: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 8,
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      overflowX: "auto",
      scrollSnapType: "x mandatory",
      margin: "0 -18px",
      padding: "0 18px",
      "-webkit-overflow-scrolling": "touch",
      "&::-webkit-scrollbar": { display: "none" },
      scrollbarWidth: "none",
    },
  },
}))(Box);

const DayCol = withStyles((theme) => ({
  root: {
    minWidth: 0,
    borderRadius: 12,
    padding: "8px 8px 10px",
    background: "#FAFBFC",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    [theme.breakpoints.down("sm")]: {
      minWidth: 132,
      flex: "0 0 auto",
      scrollSnapAlign: "start",
    },
  },
}))(Box);

const parseWall = (hhmm) => {
  const [h, m] = (hhmm || "0:0").split(":").map((n) => parseInt(n, 10) || 0);
  return h * 60 + m;
};

const wallLabel = (hhmm) => {
  const mins = parseWall(hhmm);
  const h24 = Math.floor(mins / 60);
  const m = mins % 60;
  const meridiem = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${meridiem}`;
};

const localDateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const WeekAgenda = () => {
  const rawEvents = Array.isArray(window.week_agenda) ? window.week_agenda : [];
  // Phones get a vertical agenda list: seven side-scrolling columns waste most
  // of the width on empty days and bury the one real event off-screen.
  const isMobile = useMediaQuery("(max-width:959.95px)");

  const days = useMemo(() => {
    const today = new Date();
    const cols = [];
    for (let i = 0; i < 7; i += 1) {
      const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
      cols.push({ date: d, key: localDateKey(d), events: [] });
    }

    rawEvents.forEach((ev) => {
      let key;
      let sortMinutes;
      let timeLabel;
      if (ev.kind === "lesson" && ev.starts_at) {
        const start = new Date(ev.starts_at);
        if (Number.isNaN(start.getTime())) return;
        key = localDateKey(start);
        sortMinutes = start.getHours() * 60 + start.getMinutes();
        timeLabel = start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
      } else if (ev.kind === "class" && ev.date) {
        key = ev.date;
        sortMinutes = parseWall(ev.wall_start);
        timeLabel = wallLabel(ev.wall_start);
      } else {
        return;
      }
      const col = cols.find((c) => c.key === key);
      if (col) col.events.push({ ...ev, sortMinutes, timeLabel });
    });

    cols.forEach((c) => c.events.sort((a, b) => a.sortMinutes - b.sortMinutes));
    return cols;
  }, [rawEvents]);

  const totalEvents = days.reduce((sum, d) => sum + d.events.length, 0);

  return (
    <StripWrap>
      <StripHeader>
        <Typography style={{ fontSize: 16, fontWeight: 600, color: "#1a1a1a" }}>
          This week
        </Typography>
        <a
          href="/lessons/view_calendar"
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#007BFF",
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            whiteSpace: "nowrap",
          }}
        >
          Open full calendar
          <ArrowForwardIcon style={{ fontSize: 15 }} />
        </a>
      </StripHeader>

      {totalEvents === 0 ? (
        <Typography style={{ fontSize: 13, color: "#666666" }}>
          Nothing scheduled in the next 7 days, the calendar is where lessons
          and classes come together.
        </Typography>
      ) : isMobile ? (
        <Box>
          {days
            .filter((day, i) => i === 0 || day.events.length > 0)
            .map((day, idx) => {
              const isToday = day.key === days[0].key;
              const dayName = day.date.toLocaleDateString([], { weekday: "short" });
              return (
                <Box
                  key={day.key}
                  style={{
                    display: "flex",
                    gap: 10,
                    padding: "8px 0",
                    borderTop: idx === 0 ? "none" : "1px solid #F3F4F6",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: isToday ? "#007BFF" : "#666666",
                      letterSpacing: 0.3,
                      width: 52,
                      flexShrink: 0,
                      paddingTop: 3,
                    }}
                  >
                    {isToday ? "Today" : `${dayName} ${day.date.getDate()}`}
                  </Typography>
                  <Box style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 5 }}>
                    {day.events.length === 0 && (
                      <Typography style={{ fontSize: 12, color: "#C4C9CF", paddingTop: 3 }}>
                        Nothing scheduled
                      </Typography>
                    )}
                    {day.events.slice(0, 4).map((ev, j) => (
                      <a key={`${day.key}-${j}`} href={ev.url} style={{ textDecoration: "none", display: "block" }}>
                        <Box
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 8,
                            borderRadius: 8,
                            padding: "5px 8px",
                            ...(ev.kind === "class"
                              ? { border: "1px dashed #BDBDBD", background: "#ffffff" }
                              : { background: "#EEF0FB", border: "1px solid #E3E6F8" }),
                          }}
                        >
                          <Typography style={{ fontSize: 11, color: "#666666", whiteSpace: "nowrap" }}>
                            {ev.timeLabel}
                          </Typography>
                          <Typography
                            style={{
                              fontSize: 12.5,
                              fontWeight: 600,
                              color: ev.kind === "class" ? "#4F4F4F" : "#3f51b5",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                            }}
                          >
                            {ev.title}
                          </Typography>
                        </Box>
                      </a>
                    ))}
                    {day.events.length > 4 && (
                      <a
                        href="/lessons/view_calendar"
                        style={{ fontSize: 11, fontWeight: 600, color: "#007BFF", textDecoration: "none" }}
                      >
                        +{day.events.length - 4} more
                      </a>
                    )}
                  </Box>
                </Box>
              );
            })}
        </Box>
      ) : (
        <DayRow>
          {days.map((day, i) => {
            const isToday = i === 0;
            const dayName = day.date.toLocaleDateString([], { weekday: "short" });
            return (
              <DayCol
                key={day.key}
                style={isToday ? { background: "#F4F8FF", border: "1px solid #DFEBFF" } : undefined}
              >
                <Typography
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: isToday ? "#007BFF" : "#666666",
                    letterSpacing: 0.3,
                  }}
                >
                  {isToday ? "Today" : `${dayName} ${day.date.getDate()}`}
                </Typography>

                {day.events.length === 0 && (
                  <Typography style={{ fontSize: 12, color: "#C4C9CF" }}>—</Typography>
                )}

                {day.events.slice(0, 4).map((ev, j) => (
                  <a
                    key={`${day.key}-${j}`}
                    href={ev.url}
                    style={{ textDecoration: "none", display: "block" }}
                  >
                    <Box
                      style={
                        ev.kind === "class"
                          ? {
                              border: "1px dashed #BDBDBD",
                              background: "#ffffff",
                              borderRadius: 8,
                              padding: "5px 7px",
                            }
                          : {
                              background: "#EEF0FB",
                              border: "1px solid #E3E6F8",
                              borderRadius: 8,
                              padding: "5px 7px",
                            }
                      }
                    >
                      <Typography style={{ fontSize: 10, color: "#666666", lineHeight: 1.2 }}>
                        {ev.timeLabel}
                      </Typography>
                      <Typography
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: ev.kind === "class" ? "#4F4F4F" : "#3f51b5",
                          lineHeight: 1.25,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {ev.title}
                      </Typography>
                    </Box>
                  </a>
                ))}

                {day.events.length > 4 && (
                  <a
                    href="/lessons/view_calendar"
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#007BFF",
                      textDecoration: "none",
                    }}
                  >
                    +{day.events.length - 4} more
                  </a>
                )}
              </DayCol>
            );
          })}
        </DayRow>
      )}
    </StripWrap>
  );
};

export default WeekAgenda;
